import axios from 'axios';
import moment from 'moment-timezone';
import { store } from 'src/main';
import { fetchAdPortfolios } from 'src/store/modules/adManager/adPortfolios/operations';
import { Option } from 'funfix-core';
import {
  IAdPortfolioAdPlatformSettingsByClient,
  IAdEntityAdPlatformSettingsByClient,
  AD_PLATFORM_SETTING_TYPE_BY_CLIENT
} from 'sl-ad-campaign-manager-data-model';
import { PartialNullable, OverrideWith } from 'src/types/utils';
import { panic } from 'src/utils/mixpanel';

const CREATE_PORTFOLIO_API_ENDPOINT = '/apiAdManager/adPlatformSettingsByClient/';

export interface IBudget {
  endDate: string | null;
}

export interface IAmsApiModel {
  creationDate: number;
  inBudget: boolean;
  lastUpdatedDate: number;
  name: string;
  portfolioId?: string;
  servingStatus: string;
  state: string;
  budget?: IBudget;
}

export interface IAutomationAttributes {
  strategyId: string;
}

export interface ICurrentMonthBudgetSetting {
  amount: number;
  budgetType: string;
  startDate: string | null;
  endDate: string | null;
}

export interface IExtendedAttributes {
  amsApiModel: IAmsApiModel;
  automationAttributes: IAutomationAttributes;
  beaconClientLoginId: number;
  entityId: string;
  entityIdApi: string;
  entityIdUi: string;
  name: string;
  nextMonthBudgetSetting?: any;
  portfolioId?: string;
  portfolioIdApi?: string;
  retailerId: number;
  currentMonthBudgetSetting?: ICurrentMonthBudgetSetting | {};
}

export type IPortfolioData = OverrideWith<
  PartialNullable<IAdPortfolioAdPlatformSettingsByClient, 'id' | 'settingId'>,
  {
    createdTime: string;
    extendedAttributes: PartialNullable<IExtendedAttributes, 'portfolioId' | 'portfolioIdApi'>;
    lastUpdatedTime: string;
  }
>;

export interface FormStateProps {
  name: string;
  entity: {
    id: string;
  };
  strategy: {
    id: string;
  };
  budgetType: {
    name: string;
  };
  budgetAmount: number;
  startDate?: string;
  endDate?: string;
  campaignIds: string[];
}

export const createPortfolio = async (formState: FormStateProps): Promise<any> => {
  const now = new Date();
  const nowISOString = now.toISOString();
  const nowUnixTimestampMS = now.getTime();
  const portfolioName = formState.name.trim();
  const { user, adPlatformSettingsByClient, retailer, adCampaigns } = store.getState();
  const { platformType } = retailer;
  const filteredCampaigns = adCampaigns.filter((campaign) => formState.campaignIds.includes(campaign.campaignId));
  const entity = adPlatformSettingsByClient.find(
    (setting) => setting.settingType === 'entityId' && setting.settingId === formState.entity.id.toString()
  ) as IAdEntityAdPlatformSettingsByClient | undefined;

  if (!entity) {
    return panic(`No ad platform setting by client found for entity id in form state: "${formState.entity}"`);
  }

  const portfolioData: IPortfolioData = {
    beaconClientId: user.config.vendor.BeaconClientId,
    createdBy: user.session.email,
    createdTime: nowISOString,
    extendedAttributes: {
      amsApiModel: {
        creationDate: nowUnixTimestampMS,
        inBudget: true,
        lastUpdatedDate: nowUnixTimestampMS,
        name: portfolioName,
        portfolioId: undefined,
        servingStatus: 'PORTFOLIO_STATUS_ENABLED',
        state: 'enabled'
      } as any,
      automationAttributes: {
        strategyId: formState.strategy.id
      },
      beaconClientLoginId: entity.extendedAttributes.beaconClientLoginId,
      entityId: entity.extendedAttributes.entityId,
      entityIdApi: entity.extendedAttributes.entityIdApi,
      entityIdUi: entity.extendedAttributes.entityIdUi,
      name: portfolioName,
      nextMonthBudgetSetting: undefined,
      portfolioId: undefined,
      portfolioIdApi: undefined,
      retailerId: parseInt(retailer.id, 10)
    },
    id: undefined,
    lastUpdatedBy: user.session.email,
    lastUpdatedTime: nowISOString,
    name: portfolioName,
    platformType,
    settingId: undefined,
    settingType: AD_PLATFORM_SETTING_TYPE_BY_CLIENT.PORTFOLIO_ID
  };

  portfolioData.extendedAttributes.currentMonthBudgetSetting = {
    amount: formState.budgetAmount,
    budgetType: formState.budgetType.name,
    startDate: Option.of(formState.startDate)
      .map(moment)
      .map((m) => m.format('YYYYMMDD'))
      .getOrElseL(() => {
        if (platformType.startsWith('AmazonAMS')) {
          return moment().format('YYYYMMDD');
        }
        return null;
      }),
    endDate: Option.of(formState.endDate)
      .map(moment)
      .map((m) => m.format('YYYYMMDD'))
      .orNull()
  };

  const endpoint = `${CREATE_PORTFOLIO_API_ENDPOINT}${filteredCampaigns.length ? 'createAdPortfolio' : 'adPortfolio'}`;

  const res = await axios.post(endpoint, {
    ...(filteredCampaigns.length ? { adPlatformSettingsByClient: portfolioData } : { ...portfolioData }),
    adCampaigns: filteredCampaigns
  });
  if (res.status !== 200) {
    return panic(`Error response when trying to create portfolio: ${JSON.stringify(res.data)}`);
  }

  store.dispatch(fetchAdPortfolios() as any);

  return res.data;
};
